import { GL_ROLES } from '../types';

/**
 * Turning the 412's role names into something a bookkeeper can act on.
 *
 * <p>The unmapped-roles message used to interpolate raw camelCase identifiers, in both
 * languages: <i>"Unmapped roles: salesRevenue, tipsPayable."</i> The translated labels for
 * exactly those keys already existed in `glMappingPage.fields.*` — in EN and ES — and were
 * unused by the one message whose entire job is to send someone to that screen.</p>
 *
 * <p>🔴 <b>Two key spaces arrive in one wire field, and that is the whole difficulty.</b>
 * `MissingGlMappingException.MissingRoles` is populated from two throw sites that do not
 * agree:</p>
 *
 * <ul>
 *   <li>`JournalTemplateService` throws `GlRoles.*` — canonical role names
 *       (`salesTaxPayable`), which are what `glMappingPage.fields.*` is keyed on.</li>
 *   <li>`XeroCsvBuilder` throws the <b>legacy typed property</b> names
 *       (`salesTaxPayableAccountCode`), pinned by six backend tests.</li>
 * </ul>
 *
 * <p>A lookup that ignored the difference would resolve the template path and silently
 * fail the DR-15 path — and because i18next returns a missing key verbatim, it would render
 * the literal `glMappingPage.fields.salesTaxPayableAccountCode`. <b>Worse than the raw
 * identifier it replaced.</b> Normalising here fixes the message without changing a wire
 * contract or the six tests that pin it; unifying the two spaces at the source is a
 * separate decision, filed rather than taken.</p>
 */

const LEGACY_SUFFIX = 'AccountCode';
const CANONICAL: ReadonlySet<string> = new Set<string>(GL_ROLES);

/** Looks up one i18n key. Matches `t`'s contract, including returning a miss verbatim. */
export type Translate = (key: string) => string;

function labelKey(role: string): string {
  return `glMappingPage.fields.${role}`;
}

/**
 * Maps a legacy `…AccountCode` name onto its canonical role, leaving everything else alone.
 *
 * <p>The suffix is stripped only when what remains is a role that actually exists. A
 * client-defined `somethingCustomAccountCode` is not evidence of a role called
 * `somethingCustom`, and inventing one would produce a lookup for a key nobody wrote.</p>
 */
export function normalizeRoleKey(raw: string): string {
  if (!raw.endsWith(LEGACY_SUFFIX)) return raw;
  const stripped = raw.slice(0, -LEGACY_SUFFIX.length);
  return CANONICAL.has(stripped) ? stripped : raw;
}

/**
 * The display label for a role name, in the active language.
 *
 * <p>Falls back to the name itself for anything without copy. `roles` keys are deliberately
 * unrestricted — a client may map a role PTC has not thought of — so an unlabelled role is
 * an ordinary state, not an error. The one thing this must never emit is an i18n key.</p>
 */
export function roleLabel(raw: string, translate: Translate): string {
  const role = normalizeRoleKey(raw);
  if (!CANONICAL.has(role)) return raw;

  const key = labelKey(role);
  const label = translate(key);
  // A canonical role with no copy would otherwise render the key. Belt and braces: the
  // premise that every role HAS copy is asserted in the tests, and this is what happens
  // if that premise ever stops holding.
  return label === key ? raw : label;
}

/**
 * The unmapped roles as one human-readable list.
 *
 * <p>De-duplicated <b>after</b> normalising, because normalisation itself can collapse two
 * wire entries onto one role, and naming the same role twice reads as two problems. Server
 * order is preserved — it follows the order the journal builds its lines.</p>
 */
export function formatMissingRoles(raw: readonly string[], translate: Translate): string {
  const seen = new Set<string>();
  const labels: string[] = [];

  for (const entry of raw) {
    const role = normalizeRoleKey(entry);
    if (seen.has(role)) continue;
    seen.add(role);
    labels.push(roleLabel(entry, translate));
  }

  return labels.join(', ');
}
